import React, { useCallback, useState } from 'react';
import { BubbleMenu as BaseBubbleMenu, Editor, useEditorState } from '@tiptap/react';
import PropTypes from 'prop-types';
import isValidUrl from '../../../../../helpers/selectors/isValidUrl';

const LinkMenu = ({ editor, appendTo }) => {
  const [showEdit, setShowEdit] = useState(false);
  const [inputUrl, setInputUrl] = useState('');
  const [openInNewTab, setOpenInNewTab] = useState(false);

  const { link, target } = useEditorState({
    editor,
    selector: (ctx) => {
      const attrs = ctx.editor.getAttributes('link');
      return { link: attrs.href, target: attrs.target };
    },
  });

  const shouldShow = useCallback(() => editor.isActive('link'), [editor]);

  const handleEdit = useCallback(() => {
    setInputUrl(link || '');
    setOpenInNewTab(target === '_blank');
    setShowEdit(true);
  }, [link, target]);

  const onSetLink = useCallback(() => {
    if (!isValidUrl(inputUrl)) return; // do nothing if not valid
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: inputUrl, target: openInNewTab ? '_blank' : '' })
      .run();
    setShowEdit(false);
  }, [editor, inputUrl, openInNewTab]);

  const onUnsetLink = useCallback(() => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setShowEdit(false);
  }, [editor]);

  return (
    <BaseBubbleMenu
      editor={editor}
      pluginKey="textMenu"
      shouldShow={shouldShow}
      updateDelay={0}
      tippyOptions={{
        popperOptions: {
          modifiers: [{ name: 'flip', enabled: false }],
        },
        appendTo: () => appendTo.current,
        onHidden: () => setShowEdit(false),
      }}
    >
      <div className="bg-white border border-gray-300 shadow-lg rounded-md p-4 w-72">
        {showEdit ? (
          <div className="flex flex-col space-y-3">
            <p className="text-sm text-gray-700 italic px-2">Start with http:// or https://</p>
            <input
              type="text"
              placeholder="Paste link"
              value={inputUrl}
              onChange={(e) => setInputUrl(e.target.value)}
              className="border px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <label className="flex items-center space-x-2" htmlFor="checkbox">
              <input
                type="checkbox"
                name="checkbox"
                checked={openInNewTab}
                onChange={(e) => setOpenInNewTab(e.target.checked)}
              />
              <span className="text-sm">Open in new tab</span>
            </label>
            <div className="flex justify-between">
              <button
                type="button"
                onClick={onSetLink}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                disabled={!isValidUrl(link)}
              >
                Apply
              </button>
              <button
                type="button"
                onClick={() => setShowEdit(false)}
                className="text-gray-500 hover:underline text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col space-y-2">
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline break-all text-sm"
            >
              {link}
            </a>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={handleEdit}
                className="text-sm text-blue-500 hover:underline"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={onUnsetLink}
                className="text-sm text-red-500 hover:underline"
              >
                Remove
              </button>
            </div>
          </div>
        )}
      </div>
    </BaseBubbleMenu>
  );
};
const DOMElement = typeof Element !== 'undefined' ? Element : () => {};

LinkMenu.propTypes = {
  editor: PropTypes.instanceOf(Editor).isRequired,
  appendTo: PropTypes.shape({ current: PropTypes.instanceOf(DOMElement) }).isRequired,
};
export default LinkMenu;
